import DataFieldCache from "../models/data.field.cache";
import {MdbsCore} from "@/api/tsclient";
import DataFieldCreateDto = MdbsCore.DataFieldCreateDto;
import DataFieldUpdateDto = MdbsCore.DataFieldUpdateDto;
import DataFieldDefinitionDto = MdbsCore.DataFieldDefinitionDto;
import DataFieldDto = MdbsCore.DataFieldDto;
import ITemporaryDataFieldDto = MdbsCore.ITemporaryDataFieldDto;
import {DEFINITIONTYPE_POPULATION, DEFINITIONTYPE_REDIRECT} from "@/auxillary/rules";

const BRANCHING_FIELD_TYPES = ["ListEntry", "EventReportListEntry"];
const PARENT_FIELD_TYPES = ["List","ListEntry", "EventReportList","EventReportListEntry", "FieldGroup"];
const ROOT_PARENT_FIELD_TYPES = ["List","EventReportList", "FieldGroup"];

export const isBranchingFieldType = function (fieldType: string | undefined) {
    return BRANCHING_FIELD_TYPES.some(f => f == fieldType);
}

export const isParentFieldType = function (fieldType: string | undefined) {
    return PARENT_FIELD_TYPES.some(f => f == fieldType);
}

export const isRootParentFieldType = function (fieldType: string | undefined) {
    return ROOT_PARENT_FIELD_TYPES.some(f => f == fieldType);
}

export const convertToCreateDtos = function (dataFields: Array<DataFieldCache>, parentDto?: DataFieldCreateDto | undefined): Array<DataFieldCreateDto> {
    const dtos = new Array<DataFieldCreateDto>()

    for (let dataField of dataFields) {
        // TODO find better algorithm to identify new fields an still have ids
        // can happen if first creation try failed and dtos return with guids
        //if (!dataField.isNewField)
        //    throw new Error("Existing data field found, can't convert to create data transfer object")

        let isVisible = dataField.isVisible
        if (parentDto?.isVisible === false)
            isVisible = false;

        const dto = new DataFieldCreateDto({
            definitionId: dataField.definitionId,
            isVisible: isVisible,
            lockedManually: dataField.isLockedManually,
            order: dataField.order,
            tags: dataField.tags,
            values: dataField.values
        })
        dto.childFields = convertToCreateDtos(dataField.childFields, dto)

        dtos.push(dto);
    }
    return dtos;
}

export const convertToDeleteDtos = function (dataFields: Array<DataFieldCache>, parentDto?: DataFieldUpdateDto | undefined): Array<DataFieldUpdateDto> {
    const removedFields = new Array<DataFieldUpdateDto>()

    const allFields = dataFields.flatMap(f =>
        f.removingChildren.traverse(f => f.removingChildren));

    for (const dataField of allFields) {
        let isVisible = dataField.isVisible
        if (parentDto?.isVisible === false)
            isVisible = false;

        const dto = new DataFieldUpdateDto({
            id: dataField.isNewField ? undefined : dataField.id,
            definitionId: dataField.definitionId,
            isVisible: isVisible,
            lockedManually: dataField.isLockedManually,
            order: dataField.order,
            tags: dataField.tags,
            values: dataField.values,
            reasonOfChange: dataField.reasonForChange
        });
        removedFields.push(dto);
    }
    return removedFields;
}

export const findFieldForChangeReason = function (fieldToFind: DataFieldCache, fieldsToSearch: Array<DataFieldCache>): DataFieldCache | undefined {
    for (const fieldToSearch of fieldsToSearch) {
        if (fieldToFind.id == fieldToSearch.id) {
            return fieldToSearch;
        }
        let foundField = findFieldForChangeReason(fieldToFind, fieldToSearch.childFields)

        if (foundField == undefined)
            foundField = findFieldForChangeReason(fieldToFind, fieldToSearch.removingChildren)

        if (foundField !== undefined) {
            return foundField;
        }
    }

    return undefined;
}

export const convertToUpdateDtos = function (dataFields: Array<DataFieldCache>, parentDto?: DataFieldUpdateDto | undefined): Array<DataFieldUpdateDto> {
    const dtos = new Array<DataFieldUpdateDto>()

    for (let dataField of dataFields) {

        let isVisible = dataField.isVisible
        if (parentDto?.isVisible === false)
            isVisible = false;

        const dto = new DataFieldUpdateDto({
            id: dataField.isNewField ? undefined : dataField.id,
            definitionId: dataField.definitionId,
            isVisible: isVisible,
            lockedManually: dataField.isLockedManually,
            order: dataField.order,
            tags: dataField.tags,
            values: dataField.values,
            reasonOfChange: dataField.reasonForChange
        });
        dto.childFields = convertToUpdateDtos(dataField.childFields, dto)

        dtos.push(dto);
    }
    return dtos;
}

const populateChildCache = function (
    parentDataField: DataFieldCache,
    parentDto: DataFieldDto | null,
    definitions: Array<Readonly<DataFieldDefinitionDto>>,
    dataFieldDtos: Array<DataFieldDto>,
    temporaryFields: Array<ITemporaryDataFieldDto>): DataFieldCache {

    const parentDefinitionId = parentDataField.definitionId;

    // cannot find child like this
    const childFieldDtos = parentDto?.childFields ?? [];
    const newChildDefinitions = definitions.filter(d => !d.isDeleted && d.parentFieldDefinitionId == parentDefinitionId && childFieldDtos.find(cf => cf.definitionId == d.id) === undefined);

    // add existing fields
    for (let dto of childFieldDtos) {
        const definition = definitions.find(d => d.id == dto.definitionId)

        if (!definition) {
            throw new Error(`data-field-definition with id: ${dto.definitionId} is missing!`);
        }
        if (definition.isDeleted || dto.isDeleted) continue;

        const isFieldDeactivated = dto.tags?.isDeactivated == true;
        const temporaryField = !isFieldDeactivated && hasPopulationOnUpdate(definition.rules, dto) ?
            temporaryFields.find(f => f.definitionId === definition.id) : undefined;

        let existingField = new DataFieldCache(definition, dto, temporaryField)
        existingField = populateChildCache(existingField, dto, definitions, dataFieldDtos, isFieldDeactivated ? []: temporaryFields);
        parentDataField.addChild(existingField);
    }

    // add missing fields
    for (let definition of newChildDefinitions) {
        if (isBranchingFieldType(definition.definitionType)) {
            continue;
        }
        const tempField = temporaryFields?.find(f => f.definitionId === definition.id);

        let newField = new DataFieldCache(definition, undefined, tempField);
        newField = populateChildCache(newField, null, definitions, dataFieldDtos, temporaryFields);
        parentDataField.addChild(newField);
    }

    return parentDataField;
}

export const populateCache = function (
    parentDataField: DataFieldCache,
    definitions: Array<Readonly<DataFieldDefinitionDto>>,
    dataFieldDtos: Array<DataFieldDto>,
    temporaryFields: Array<ITemporaryDataFieldDto>): DataFieldCache {

    const parentDefinitionId = parentDataField.definitionId;

    // cannot find child like this
    const childFieldDtos = dataFieldDtos.find(d => d.id == parentDataField.id)?.childFields ?? [];
    const newChildDefinitions = definitions.filter(d => !d.isDeleted && d.parentFieldDefinitionId == parentDefinitionId && childFieldDtos.find(cf => cf.definitionId == d.id) === undefined);

    // add existing fields
    for (let dto of childFieldDtos) {
        const definition = definitions.find(d => d.id == dto.definitionId)

        if (!definition) {
            throw new Error(`data-field-definition with id: ${dto.definitionId} is missing!`);
        }

        if (definition.isDeleted || dto.isDeleted) continue;

        const isFieldDeactivated = dto.tags?.isDeactivated == true;
        const temporaryField = !isFieldDeactivated && hasPopulationOnUpdate(definition.rules, dto) ?
            temporaryFields.find(f => f.definitionId === definition.id) : undefined;

        let existingField = new DataFieldCache(definition, dto, temporaryField)
        existingField = populateChildCache(existingField, dto, definitions, dataFieldDtos, isFieldDeactivated? [] : temporaryFields);
        parentDataField.addChild(existingField);
    }

    // add missing fields
    for (let definition of newChildDefinitions) {
        if (isBranchingFieldType(definition.definitionType)) {
            continue;
        }
        const tempField = temporaryFields.find(f => f.definitionId === definition.id);

        let newField = new DataFieldCache(definition, undefined, tempField);
        newField = populateChildCache(newField, null, definitions, dataFieldDtos,  temporaryFields);
        parentDataField.addChild(newField);
    }

    return parentDataField;
}

export const hasPopulationOnUpdate = function (rules: MdbsCore.RuleDefinitionDto[] | undefined, dto: MdbsCore.DataFieldDto) {
    if (dto.locked || !rules)
        return false;

    const populationRule = rules?.find(r => r.definitionType == DEFINITIONTYPE_POPULATION && !r.isDeleted);

    return populationRule && populationRule.definition?.onUpdate;
}

export const findField = function (fieldToFind: DataFieldCache, fieldsToSearch: Array<DataFieldCache>): DataFieldCache | undefined {

    for (let fieldToSearch of fieldsToSearch) {
        if (fieldToFind.id == fieldToSearch.id) {
            return fieldToSearch;
        }
        let foundField = findField(fieldToFind, fieldToSearch.childFields)
        if (foundField !== undefined) {
            return foundField;
        }
    }

    return undefined;
}

export const findParentField = function (fieldToFind: DataFieldCache, fieldsToSearch: Array<DataFieldCache>): DataFieldCache | undefined {

    for (let fieldToSearch of fieldsToSearch) {
        // search children if we found the patend
        if (fieldToSearch.childFields?.find(f => f.id == fieldToFind.id))
            return fieldToSearch

        let foundField = findParentField(fieldToFind, fieldToSearch.childFields)
        if (foundField !== undefined) {
            return foundField;
        }
    }

    return undefined;
}

export const updateFieldsEditable = function (fields: Array<DataFieldCache>, editable: boolean): void {
    updateEditableRecursively(fields, editable);
}

const updateEditableRecursively = function (fields: Array<DataFieldCache>, editable: boolean): void {
    if (fields == null)
        return;

    for (let field of fields) {
        field.isEditable = editable;

        updateEditableRecursively(field.childFields, editable);
    }
}

export const updateFieldVisibility = function(fields: Array<DataFieldCache>){
    for (let dataField of fields.traverse(f => f.childFields)) {
        const isVisible = dataField.isVisible;
        dataField.isVisible = isVisible;
    }
}

export const findRedirectRuleFields = function (fieldsToSearch: Array<DataFieldCache>): Array<DataFieldCache> {
    let result = new Array<DataFieldCache>();

    for (let fieldToSearch of fieldsToSearch) {
        if (fieldToSearch.isVisible &&
            fieldToSearch.rules.some(r => !r.isDeleted && r.definitionType == DEFINITIONTYPE_REDIRECT)) {
            result.push(fieldToSearch);
        }

        const childResult = findRedirectRuleFields(fieldToSearch.childFields);
        result = result.concat(childResult);
    }

    return result;
}



